// Vista de cuentas (Fase 4): fase, soluciones, salud, cobertura y última actividad, para ver cuál negocio se está
// apagando antes de la semana 3. Lo que llega acá son conteos y fechas; los datos de un negocio piden un acceso
// de soporte con motivo, que queda en su bitácora (§10).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Cargando, Input, Modal, PageHead, Select, Tabla, Tag, TarjetaIndicador, Vacio } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { desde, momento } from '../lib/formato';
import { useSesion } from '../lib/sesion';

const SALUD: Record<string, any> = { ok: 'ok', pending: 'pending', error: 'error', neutral: 'neutral' };

export function Cuentas() {
  const q = useQuery({ queryKey: ['panel'], queryFn: () => api<any>('/api/panel'), refetchInterval: 120_000 });
  const [abierta, setAbierta] = useState<any>(null);
  if (q.isLoading || !q.data) return <Cargando />;
  const { cuentas, resumen } = q.data;

  return (
    <div className="page">
      <PageHead title="Cuentas" subtitle="Todos los negocios a la vez, sin entrar a ninguno. Lo que se ve acá son conteos y fechas; para mirar los datos de uno hace falta abrir un acceso de soporte con el motivo escrito." />
      <div className="kpis">
        <TarjetaIndicador label="Negocios" value={resumen.cuentas} trend={`${cuentas.filter((c: any) => c.soluciones > 0).length} con alguna solución instalada`} />
        <TarjetaIndicador label="Se están apagando" value={resumen.apagandose} tone={resumen.apagandose ? 'error' : 'ok'} trend="Sin datos hace más de 3 días, o con una solución caída" />
        <TarjetaIndicador label="Esperando una persona" value={resumen.cuarentena + resumen.aprobaciones} tone={resumen.cuarentena + resumen.aprobaciones ? 'pending' : 'ok'} trend={`${resumen.cuarentena} en cuarentena · ${resumen.aprobaciones} por aprobar`} />
        <TarjetaIndicador
          label="Horas del equipo por cuenta"
          value={resumen.horasPorCuenta === null ? '—' : `${resumen.horasPorCuenta} h`}
          tone={resumen.horasPorCuenta === null ? 'neutral' : resumen.horasPorCuenta <= 2 ? 'ok' : 'pending'}
          trend="Al mes · la medida de la Fase 4"
        />
      </div>

      <Tabla
        rowKey="id"
        rows={cuentas}
        onRowClick={(c: any) => setAbierta(c)}
        columns={[
          { key: 'nombre', label: 'Negocio', render: (c: any) => (<><strong>{c.nombre}</strong><div className="muted" style={{ fontSize: 12 }}>{c.tipo}{c.ciudad ? ` · ${c.ciudad}` : ''} · {c.administrador ?? 'sin administrador'}</div></>) },
          { key: 'fase', label: 'Fase', render: (c: any) => <Tag>{`Fase ${c.fase}`}</Tag> },
          {
            key: 'soluciones', label: 'Soluciones', render: (c: any) => (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {c.soluciones_detalle.length === 0 && <span className="muted">Ninguna</span>}
                {c.soluciones_detalle.map((s: any) => <Badge key={s.slug} state={SALUD[s.salud]}>{s.nombre}</Badge>)}
              </div>
            ),
          },
          { key: 'cobertura', label: 'Cobertura', numeric: true, render: (c: any) => (c.cobertura === null ? <span className="muted">—</span> : <strong style={{ color: c.cobertura >= 85 ? undefined : 'var(--amber)' }}>{c.cobertura}%</strong>) },
          { key: 'ultimo_registro', label: 'Último dato', render: (c: any) => <span className={c.apagandose ? 'error-texto' : undefined}>{desde(c.ultimo_registro)}</span> },
          { key: 'pendientes', label: 'Pendientes', numeric: true, render: (c: any) => (c.cuarentena + c.aprobaciones || <span className="muted">—</span>) },
          { key: 'equipo', label: 'Mano del equipo', numeric: true, render: (c: any) => (c.intervenidos_30d ? `${c.intervenidos_30d} registros` : <span className="muted">Ninguna</span>) },
        ]}
      />
      {abierta && <Negocio cuenta={abierta} onClose={() => setAbierta(null)} />}
    </div>
  );
}

function Negocio({ cuenta, onClose }: { cuenta: any; onClose: () => void }) {
  const qc = useQueryClient();
  const { aviso } = useSesion();
  const [motivo, setMotivo] = useState('');
  const accesos = useQuery({ queryKey: ['panel-soporte'], queryFn: () => api<any[]>('/api/panel/soporte') });
  const vigente = accesos.data?.find((a) => a.cuenta_id === cuenta.id && !a.cerrado_en && new Date(a.expira_en) > new Date());
  const vista = useQuery({ queryKey: ['panel-cuenta', cuenta.id], queryFn: () => api<any>(`/api/panel/cuentas/${cuenta.id}`), enabled: Boolean(vigente) });

  const abrir = useMutation({
    mutationFn: () => api(`/api/panel/cuentas/${cuenta.id}/soporte`, { cuerpo: { motivo } }),
    onSuccess: () => { aviso('Acceso abierto. El administrador lo ve en su bitácora.'); void qc.invalidateQueries(); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const cerrar = useMutation({
    mutationFn: () => api(`/api/panel/soporte/${vigente!.id}/cerrar`, { cuerpo: {} }),
    onSuccess: (r: any) => { aviso(`Acceso cerrado · ${r.minutos} min anotados`); void qc.invalidateQueries(); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const fase = useMutation({
    mutationFn: (f: number) => api(`/api/panel/cuentas/${cuenta.id}/fase`, { cuerpo: { fase: f, motivo: 'Cambio de fase desde el panel del equipo' } }),
    onSuccess: () => { aviso('Fase cambiada. Queda en la bitácora del negocio.'); void qc.invalidateQueries({ queryKey: ['panel'] }); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const banderas = useMutation({
    mutationFn: (modulos: Record<string, boolean>) => api(`/api/panel/cuentas/${cuenta.id}/banderas`, { cuerpo: { modulos } }),
    onSuccess: () => { aviso('Funciones actualizadas.'); void qc.invalidateQueries({ queryKey: ['panel'] }); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  const modulos = { ...(cuenta.modulos ?? {}) } as Record<string, boolean>;

  return (
    <Modal title={cuenta.nombre} subtitle={`${cuenta.tipo}${cuenta.ciudad ? ` · ${cuenta.ciudad}` : ''} · administrador ${cuenta.administrador ?? '—'}`} onClose={onClose} width={860}>
      <div className="kpis">
        <TarjetaIndicador label="Registros en 7 días" value={cuenta.registros_7d} trend={`${cuenta.dias_con_dato_14} de los últimos 14 días con dato`} />
        <TarjetaIndicador label="Del catálogo" value={`${cuenta.del_catalogo} de ${cuenta.soluciones}`} trend={`${cuenta.de_cero} construidas de cero`} />
        <TarjetaIndicador label="Última actividad" value={desde(cuenta.ultima_actividad)} trend={`${cuenta.personas} personas · ${cuenta.tokens_activos} tokens activos`} />
      </div>

      <div className="eyebrow" style={{ marginTop: 18 }}>Configuración que lleva el equipo</div>
      <div className="card" style={{ padding: 16 }}>
        <div className="grid-2">
          <Select label="Fase habilitada" value={String(cuenta.fase)} options={[0, 1, 2, 3, 4, 5].map((f) => ({ value: String(f), label: `Fase ${f}` }))} onChange={(e) => fase.mutate(Number(e.target.value))} />
          <div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>Buzón de facturas</div>
            <code style={{ fontSize: 12 }}>{cuenta.buzon}@facturas.glubi.co</code>
          </div>
        </div>
        <div className="muted" style={{ fontSize: 12, margin: '12px 0 6px' }}>Funciones abiertas para este negocio</div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {['catalogo', 'agentes', 'consola', 'salida'].map((m) => (
            <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <input type="checkbox" checked={modulos[m] ?? true} onChange={(e) => banderas.mutate({ ...modulos, [m]: e.target.checked })} />
              {m}
            </label>
          ))}
        </div>
      </div>

      <div className="eyebrow" style={{ marginTop: 18 }}>Ver la cuenta</div>
      {!vigente ? (
        <div className="card" style={{ padding: 16 }}>
          <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
            Solo lectura, con el motivo escrito, anotado en la bitácora del negocio y con vencimiento. El equipo no edita datos de un cliente ni ve credenciales de conectores.
          </p>
          <Input label="Motivo del acceso" value={motivo} onChange={(e) => setMotivo(e.target.value)} helper="Lo lee el administrador en su bitácora, así que va en sus palabras" />
          <Button icon="shield" onClick={() => abrir.mutate()} disabled={abrir.isPending || motivo.trim().length < 10}>Abrir acceso de soporte</Button>
        </div>
      ) : (
        <>
          <div className="aviso-caja" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ flex: 1 }}>Acceso abierto por {vigente.nombre} · {vigente.motivo} · vence {momento(vigente.expira_en)}</span>
            <Button size="sm" variant="ghost" onClick={() => cerrar.mutate()} disabled={cerrar.isPending}>Cerrar el acceso</Button>
          </div>
          {vista.isLoading && <Cargando />}
          {vista.data && (
            <>
              <Tabla rowKey="slug" rows={vista.data.soluciones}
                columns={[
                  { key: 'nombre', label: 'Solución' },
                  { key: 'salud', label: 'Salud', render: (s: any) => <Badge state={SALUD[s.salud.estado]}>{s.salud.etiqueta}</Badge> },
                  { key: 'cobertura', label: 'Cobertura', numeric: true, render: (s: any) => (s.cobertura.pct === null ? '—' : `${s.cobertura.pct}%`) },
                  { key: 'intervenidos', label: 'Días intervenidos', numeric: true },
                ]} />
              <div className="eyebrow" style={{ marginTop: 16 }}>Cuarentena</div>
              {!vista.data.cuarentena.length ? <Vacio titulo="Nada en cuarentena">Todo lo que entró pasó las revisiones.</Vacio> : (
                <Tabla rowKey="id" rows={vista.data.cuarentena}
                  columns={[
                    { key: 'entidad', label: 'Dato' },
                    { key: 'via', label: 'Por dónde' },
                    { key: 'motivos', label: 'Por qué quedó afuera', render: (c: any) => c.motivos.join(' · ') },
                    { key: 'creado_en', label: 'Cuándo', render: (c: any) => momento(c.creado_en) },
                  ]} />
              )}
              <div className="eyebrow" style={{ marginTop: 16 }}>Bitácora</div>
              <Tabla rowKey="id" rows={vista.data.bitacora.slice(0, 25)}
                columns={[
                  { key: 'en', label: 'Cuándo', render: (b: any) => momento(b.en) },
                  { key: 'actor', label: 'Quién', render: (b: any) => b.actor?.nombre },
                  { key: 'accion', label: 'Qué pasó', render: (b: any) => (<><div>{b.accion}</div>{b.detalle && <div className="muted" style={{ fontSize: 12 }}>{b.detalle}</div>}</>) },
                ]} />
            </>
          )}
        </>
      )}
    </Modal>
  );
}
